import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { ProviderPreviewMetadataComponent } from '../../../shared/components/contribution-steps/preview-metadata/preview-metadata.component';
import { CatalogueApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

export interface PublishAssetData {
  assetName: string;
  assetType: string;
  permission: string;
  assetStatus: string;
  publishedOn: string;
  action: string;
  originalData?: any;
}

@Component({
  selector: 'app-publish-assets',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ModalComponent,
    ProviderPreviewMetadataComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './publish-assets.component.html',
  styleUrl: './publish-assets.component.scss',
})
export class PublishAssetsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('publishAssetActionCell', { static: false }) publishAssetActionCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetPermissionCell', { static: false }) publishAssetPermissionCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetPublishedOnCell', { static: false }) publishAssetPublishedOnCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetExpandCell', { static: false }) publishAssetExpandCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetChildRow', { static: false }) publishAssetChildRowTemplate!: TemplateRef<any>;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private router: Router,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  publishAssetColumns: TableColumn[] = [
    { key: 'expand', label: '', sortable: false, width: '40px' },
    { key: 'assetName', label: 'Asset Name', sortable: true },
    { key: 'assetType', label: 'Asset Type', sortable: false },
    { key: 'permission', label: 'Permission', sortable: true },
    { key: 'publishedOn', label: 'Published On', sortable: true },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  publishAssetData: PublishAssetData[] = [];
  publishAssetPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  publishAssetLoading = false;
  publishAssetEmptyMessage = 'No publish assets found';

  // Sort configuration
  currentSort: { field: string; order: 'asc' | 'desc' } = { field: 'publishedOn', order: 'desc' };

  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number } = { top: 0, left: 0 };
  
  // Track expanded rows for child row functionality
  expandedRows: Set<string> = new Set();

  // Modal state for modify asset
  showModifyModal: boolean = false;
  currentModifyAsset: PublishAssetData | null = null;
  modifyAssetData: any = null;
  isLoadingAssetData: boolean = false;

  // Tab state: Approved = ACTIVE, Pending = PENDING, Rejected = DECLINED
  activeTab: 'approved' | 'pending' | 'rejected' = 'approved';

  /** Total count per tab (from API paginationInfo.totalCount) */
  tabCounts: { approved: number; pending: number; rejected: number } = {
    approved: 0,
    pending: 0,
    rejected: 0
  };

  /** Count for the currently active tab (displayed as Total Count on the right of tabs) */
  get totalTabCount(): number {
    switch (this.activeTab) {
      case 'approved':
        return this.tabCounts.approved ?? 0;
      case 'pending':
        return this.tabCounts.pending ?? 0;
      case 'rejected':
        return this.tabCounts.rejected ?? 0;
      default:
        return 0;
    }
  }

  ngOnInit(): void {
    this.loadPublishAssets();
    this.loadAllTabCounts();
  }

  /** Fetch totalCount for all three tabs so counts show without switching. */
  private loadAllTabCounts(): void {
    const sort = 'itemCreatedAt:desc';
    Promise.all([
      this.catalogueApiService.OrgPublishAssets(1, 1, sort, ['ACTIVE']),
      this.catalogueApiService.OrgPublishAssets(1, 1, sort, ['PENDING']),
      this.catalogueApiService.OrgPublishAssets(1, 1, sort, ['DECLINED'])
    ]).then(([approvedRes, pendingRes, rejectedRes]) => {
      this.tabCounts.approved = this.getTotalCountFromResponse(approvedRes);
      this.tabCounts.pending = this.getTotalCountFromResponse(pendingRes);
      this.tabCounts.rejected = this.getTotalCountFromResponse(rejectedRes);
      this.cdr.detectChanges();
    }).catch(() => {
      // On error, counts stay 0; main table load already updates active tab count
    });
  }

  private getTotalCountFromResponse(response: any): number {
    if (!response) return 0;
    return response['paginationInfo']?.totalCount ?? response['totalCount'] ?? response['total'] ?? 0;
  }

  setActiveTab(tab: 'approved' | 'pending' | 'rejected'): void {
    this.activeTab = tab;
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: 1 };
    this.loadPublishAssets();
  }

  /** Get publishStatus filter based on active tab */
  private getStatusFilterForTab(): string[] {
    switch (this.activeTab) {
      case 'approved':
        return ['ACTIVE'];
      case 'pending':
        return ['PENDING'];
      case 'rejected':
        return ['DECLINED'];
      default:
        return ['PENDING'];
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.publishAssetExpandCellTemplate) {
        const publishAssetExpandColumn = this.publishAssetColumns.find(col => col.key === 'expand');
        if (publishAssetExpandColumn) {
          publishAssetExpandColumn.cellTemplate = this.publishAssetExpandCellTemplate;
        }
      }
      if (this.publishAssetActionCellTemplate) {
        const publishAssetActionColumn = this.publishAssetColumns.find(col => col.key === 'action');
        if (publishAssetActionColumn) {
          publishAssetActionColumn.cellTemplate = this.publishAssetActionCellTemplate;
        }
      }
      if (this.publishAssetPermissionCellTemplate) {
        const publishAssetPermissionColumn = this.publishAssetColumns.find(col => col.key === 'permission');
        if (publishAssetPermissionColumn) {
          publishAssetPermissionColumn.cellTemplate = this.publishAssetPermissionCellTemplate;
        }
      }
      if (this.publishAssetPublishedOnCellTemplate) {
        const publishAssetPublishedOnColumn = this.publishAssetColumns.find(col => col.key === 'publishedOn');
        if (publishAssetPublishedOnColumn) {
          publishAssetPublishedOnColumn.cellTemplate = this.publishAssetPublishedOnCellTemplate;
        }
      }
    }, 100);
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  /** Permission display label for chips (OPEN/PUBLIC -> Public, etc.). */
  getPermissionLabel(permission: string): string {
    if (!permission) return 'Public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'Public';
    if (p === 'RESTRICTED') return 'Restricted';
    if (p === 'PRIVATE') return 'Private';
    return permission;
  }

  /** CSS class for permission chip. */
  getPermissionChipClass(permission: string): string {
    if (!permission) return 'contribution-chip contribution-chip--public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'contribution-chip contribution-chip--public';
    if (p === 'RESTRICTED') return 'contribution-chip contribution-chip--restricted';
    if (p === 'PRIVATE') return 'contribution-chip contribution-chip--private';
    return 'contribution-chip';
  }

  /** Format Published On as DD-MMM-YYYY (e.g. 27-Jan-2026). */
  formatPublishedOn(dateValue: string): string {
    if (!dateValue) return '—';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue;
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /** Status display label: Pending, Active, Rejected, etc. */
  getStatusLabel(status: string): string {
    if (!status) return '—';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED') return 'Granted';
    if (s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'Active';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DRAFT') return 'Draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED' || s === 'DECLINED') return 'Rejected';
    return status;
  }

  /** CSS class for status chip (Pending = yellow, Granted = green, Rejected = red). */
  getStatusChipClass(status: string): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'contribution-chip contribution-chip--status-active';
    if (s === 'PENDING') return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT') return 'contribution-chip contribution-chip--status-draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED' || s === 'DECLINED') return 'contribution-chip contribution-chip--status-rejected';
    return 'contribution-chip contribution-chip--status-default';
  }

  /** Normalize API item to PublishAssetData; handles type formatting. */
  private mapItemToPublishAsset(item: any): PublishAssetData {
    const rawType = item.type || item.assetType || item.asset_type || '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    const permissionRaw = item.permission || item.accessPolicy || item.access_policy || 'PUBLIC';
    const statusRaw = item.publishStatus || item.publish_status || item.status || 'PENDING';
    return {
      assetName: item.name || item.assetName || item.asset_name || '',
      assetType: this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr,
      permission: permissionRaw,
      assetStatus: statusRaw,
      publishedOn: item.itemCreatedAt || item.item_created_at || item.createdAt || item.created_at || '',
      action: '',
      originalData: item
    };
  }

  async loadPublishAssets(): Promise<void> {
    this.publishAssetLoading = true;
    try {
      const page = this.publishAssetPagination.currentPage;
      const size = this.publishAssetPagination.pageSize;
      const statuses = this.getStatusFilterForTab();
      const sort = `${this.getApiSortField(this.currentSort.field)}:${this.currentSort.order}`;

      const response = await this.catalogueApiService.OrgPublishAssets(
        page,
        size,
        sort,
        statuses
      );

      let mappedData: PublishAssetData[] = [];
      let totalItems = 0;

      if (response) {
        if (response['result'] && Array.isArray(response['result'])) {
          mappedData = response['result'].map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = mappedData.length;
        }
      }

      this.publishAssetData = mappedData;
      this.publishAssetPagination = { 
        ...this.publishAssetPagination, 
        totalItems: totalItems 
      };
      // Update count for current tab
      this.tabCounts[this.activeTab] = totalItems;
    } catch (error) {
      console.error('Error loading publish assets:', error);
      this.publishAssetData = [];
      this.publishAssetPagination = { ...this.publishAssetPagination, totalItems: 0 };
    } finally {
      this.publishAssetLoading = false;
      // Ensure filter panel and multiselect components are properly initialized after reload
      // Use setTimeout to let Angular finish the current change detection cycle
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);
    }
  }

  onPublishAssetPageChange(page: number): void {
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: page };
    this.loadPublishAssets();
  }

  onPublishAssetPageSizeChange(pageSize: number): void {
    this.publishAssetPagination = { ...this.publishAssetPagination, pageSize: pageSize, currentPage: 1 };
    this.loadPublishAssets();
  }

  onPublishAssetSortChange(sort: { column: string; direction: 'asc' | 'desc' }): void {
    this.currentSort = { field: sort.column, order: sort.direction };
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: 1 };
    this.loadPublishAssets();
  }

  private getApiSortField(field: string): string {
    const fieldMapping: { [key: string]: string } = {
      assetName: 'name',
      permission: 'accessPolicy',
      publishedOn: 'itemCreatedAt'
    };
    return fieldMapping[field] || field;
  }

  toggleActionDropdown(event: Event, row: PublishAssetData): void {
    event.stopPropagation();
    const rowId = row.originalData?.id || JSON.stringify(row);
    
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
    } else {
      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();
      
      this.dropdownPosition = {
        top: rect.bottom + 4,
        left: rect.right - 160
      };
      
      this.openDropdownId = rowId;
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: PublishAssetData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Index in publishAssetData of the row whose action dropdown is open; null when none. */
  get openPublishAssetDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.publishAssetData.findIndex(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  /**
   * Toggle row expansion for child rows
   */
  toggleRowExpansion(row: PublishAssetData): void {
    const rowId = row.originalData?.id || JSON.stringify(row);
    if (this.expandedRows.has(rowId)) {
      this.expandedRows.delete(rowId);
    } else {
      this.expandedRows.add(rowId);
    }
  }

  /**
   * Check if a row is expanded
   */
  isRowExpanded(row: PublishAssetData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.expandedRows.has(rowId);
  }

  /**
   * Get the bound isRowExpanded function for passing to child component
   */
  getIsRowExpanded(): (row: any) => boolean {
    return (row: any) => this.isRowExpanded(row);
  }

  /** Whether to show an action in the Publish Assets dropdown based on active tab. */
  showPublishAssetAction(action: 'view' | 'approve' | 'reject' | 'modify' | 'delete'): boolean {
    switch (action) {
      case 'view':
        return true;
      case 'approve':
      case 'reject':
        return this.activeTab === 'pending';
      case 'modify':
      case 'delete':
        return this.activeTab === 'approved';
      default:
        return false;
    }
  }

  handleAction(action: string, row: PublishAssetData): void {
    this.closeActionDropdown();
    console.log(`${action} action for:`, row);

    if (action === 'approve') {
      this.approvePublishAsset(row);
    } else if (action === 'reject') {
      this.rejectPublishAsset(row);
    } else if (action === 'view') {
      this.viewPublishAsset(row);
    } else if (action === 'modify') {
      this.openModifyModal(row);
    } else if (action === 'delete') {
      this.deletePublishAsset(row);
    }
  }

  private async approvePublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      console.warn('Unable to approve asset: missing asset id', row);
      this.toasterService.error('Unable to approve asset: missing asset ID');
      return;
    }

    try {
      const payload = {
        publishStatus: 'ACTIVE',
        dataUploadStatus: true
      };

      await this.catalogueApiService.postPublishAssets(assetId, payload);
      
      this.toasterService.success('Asset has been approved successfully');
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error approving asset:', error);
      this.toasterService.error('Failed to approve asset. Please try again.');
    }
  }

  private async deletePublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      this.toasterService.error('Unable to delete asset: missing asset ID');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this asset? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await this.catalogueApiService.deleteItem(assetId);
      this.toasterService.success('Asset has been deleted successfully');
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error deleting asset:', error);
      this.toasterService.error('Failed to delete asset. Please try again.');
    }
  }

  private async rejectPublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      console.warn('Unable to reject asset: missing asset id', row);
      this.toasterService.error('Unable to reject asset: missing asset ID');
      return;
    }

    try {
      const payload = {
        publishStatus: 'DECLINED',
        dataUploadStatus: false
      };

      await this.catalogueApiService.postPublishAssets(assetId, payload);
      
      this.toasterService.success('Asset has been rejected successfully');
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error rejecting asset:', error);
      this.toasterService.error('Failed to reject asset. Please try again.');
    }
  }

  private viewPublishAsset(row: PublishAssetData): void {
    const original = row.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];

    if (!assetId) {
      console.warn('Unable to navigate to asset details: missing asset id', row);
      this.toasterService.error('Unable to view asset: missing asset ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType = original.type || original.assetType || original.asset_type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();
      
      if (rawTypeLower.includes('databank') || rawTypeLower.includes('database')) {
        routeSegment = 'dataset';
      } else if (rawTypeLower.includes('aimodel') || rawTypeLower.includes('model')) {
        routeSegment = 'model';
      } else if (rawTypeLower.includes('app') || rawTypeLower.includes('usecase')) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      // Open in new tab
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, assetId])
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error('Unable to determine asset type for navigation');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize', ['$event'])
  onWindowScrollOrResize(): void {
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }

  /**
   * Open modify modal and load asset data
   */
  async openModifyModal(row: PublishAssetData): Promise<void> {
    console.log('=== PUBLISH-ASSETS: openModifyModal CALLED ===');
    console.log('PUBLISH-ASSETS: row:', row);
    
    const assetId = row.originalData?.id;
    console.log('PUBLISH-ASSETS: assetId:', assetId);

    if (!assetId) {
      console.warn('PUBLISH-ASSETS: Unable to modify asset - missing asset id', row);
      this.toasterService.error('Unable to modify asset: missing asset ID');
      return;
    }

    this.isLoadingAssetData = true;
    this.currentModifyAsset = row;

    try {
      // Fetch full asset details
      console.log('PUBLISH-ASSETS: Fetching asset details...');
      const response = await this.catalogueApiService.getItemById(assetId);
      console.log('PUBLISH-ASSETS: getItemById response:', response);
      
      // Extract asset data from response
      let assetData = response;
      if (response?.result) {
        assetData = Array.isArray(response.result) ? response.result[0] : response.result;
      } else if (response?.data) {
        assetData = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        assetData = response[0];
      }

      if (!assetData) {
        throw new Error('No asset data found in response');
      }

      console.log('PUBLISH-ASSETS: Extracted assetData:', assetData);

      // Map asset data to preview-metadata format
      this.modifyAssetData = this.mapAssetDataToPreviewFormat(assetData, row.assetType);
      console.log('PUBLISH-ASSETS: Mapped modifyAssetData:', this.modifyAssetData);
      
      this.showModifyModal = true;
      console.log('PUBLISH-ASSETS: Modal opened, showModifyModal:', this.showModifyModal);
    } catch (error) {
      console.error('PUBLISH-ASSETS: Error loading asset data for modification:', error);
      this.toasterService.error('Failed to load asset data. Please try again.');
      this.closeModifyModal();
    } finally {
      this.isLoadingAssetData = false;
    }
  }

  /**
   * Close modify modal
   */
  closeModifyModal(): void {
    this.showModifyModal = false;
    this.currentModifyAsset = null;
    this.modifyAssetData = null;
    this.isLoadingAssetData = false;
  }

  /**
   * Get asset type for modify modal
   */
  getAssetTypeForModify(): 'dataset' | 'model' | null {
    if (!this.currentModifyAsset) return null;
    const typeLabel = (this.currentModifyAsset.assetType || '').toLowerCase();
    if (typeLabel === 'model') return 'model';
    if (typeLabel === 'dataset') return 'dataset';
    return null;
  }

  /**
   * Ensure minimum length for validation (pads with spaces if needed)
   */
  private ensureMinLength(value: string, minLength: number = 50): string {
    if (!value) return '';
    return value.length < minLength ? value.padEnd(minLength, ' ') : value;
  }

  /**
   * Map API response to preview-metadata expected format
   */
  private mapAssetDataToPreviewFormat(apiData: any, assetType: string): any {
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - apiData:', apiData);
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - assetType:', assetType);

    const isModel = assetType.toLowerCase().includes('model') ||
                    (apiData.type && Array.isArray(apiData.type) && apiData.type.some((t: string) => t.includes('AiModel'))) ||
                    (apiData.type && typeof apiData.type === 'string' && apiData.type.includes('AiModel'));

    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - isModel:', isModel);

    // Map access policy
    const accessPolicy = apiData.accessPolicy || apiData.access_policy || 'OPEN';
    const permission = accessPolicy === 'OPEN' ? 'Open' : 
                       accessPolicy === 'RESTRICTED' ? 'Restricted' : 'Private';

    // Parse year range - handle both string and object formats
    let yearRange = apiData.yearRange || apiData.year_range || null;
    console.log('PUBLISH-ASSETS: yearRange (raw):', yearRange);
    
    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0]) || null,
          endYear: parseInt(parts[1]) || null
        };
      }
    } else if (typeof yearRange === 'object' && yearRange !== null && !yearRange.startYear) {
      // If it's an object but doesn't have startYear/endYear, try to convert
      yearRange = null;
    }
    console.log('PUBLISH-ASSETS: yearRange (parsed):', yearRange);

    // Handle resourceServer - extract URL from array if needed
    let resourceServerUrl = '';
    const rsData = apiData.resourceServer || apiData.resource_server;
    console.log('PUBLISH-ASSETS: resourceServer (raw):', rsData);
    
    if (Array.isArray(rsData) && rsData.length > 0) {
      resourceServerUrl = rsData[0]?.url || '';
    } else if (typeof rsData === 'string') {
      resourceServerUrl = rsData;
    } else if (rsData && typeof rsData === 'object' && rsData.url) {
      resourceServerUrl = rsData.url;
    }
    console.log('PUBLISH-ASSETS: resourceServerUrl (extracted):', resourceServerUrl);

    // Get short and long descriptions, ensuring minimum length for validation
    const shortDesc = apiData.shortDescription || apiData.short_description || '';
    const longDesc = apiData.description || apiData.longDescription || '';

    const mappedData = {
      name: apiData.name || apiData.label || '',
      title: apiData.name || apiData.label || '',
      permission: permission,
      accessPolicy: accessPolicy,
      shortDescription: this.ensureMinLength(shortDesc, 50),
      description: this.ensureMinLength(longDesc, 50),
      longDescription: this.ensureMinLength(longDesc, 50),
      license: apiData.license || 'Open Government License, India',
      otherLicense: apiData.otherLicense || apiData.other_license || '',
      resourceServer: resourceServerUrl,
      uploadFrequency: apiData.uploadFrequency || apiData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: apiData.geoCoverage || apiData.geo_coverage || '',
      industry: apiData.industry || 'Agriculture',
      datasetType: isModel ? null : (apiData.dataBankType || apiData.datasetType || 'Structured'),
      fileFormat: apiData.fileFormat || apiData.file_format || 'csv',
      tags: Array.isArray(apiData.tags) ? apiData.tags : (apiData.tags ? [apiData.tags] : []),
      sampleDataUrl: apiData.mediaURL || apiData.media_url || apiData.sampleDataUrl || '',
      dataDescriptors: apiData.dataDescriptor || apiData.data_descriptor || [],
      type: apiData.type || []
    };

    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - mappedData:', mappedData);
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - mappedData (JSON):', JSON.stringify(mappedData, null, 2));

    return mappedData;
  }

  /**
   * Handle form submission from preview-metadata component
   */
  async onModifyFormSubmit(formData: any): Promise<void> {
    console.log('=== PUBLISH-ASSETS: onModifyFormSubmit CALLED ===');
    console.log('formData:', formData);
    console.log('currentModifyAsset:', this.currentModifyAsset);

    if (!this.currentModifyAsset) {
      console.warn('PUBLISH-ASSETS: No currentModifyAsset - returning early');
      return;
    }

    const assetId = this.currentModifyAsset.originalData?.id;
    console.log('PUBLISH-ASSETS: assetId:', assetId);

    if (!assetId) {
      console.error('PUBLISH-ASSETS: Missing asset ID');
      this.toasterService.error('Unable to update asset: missing asset ID');
      return;
    }

    try {
      // Build payload for updateItem API
      console.log('PUBLISH-ASSETS: Building update payload...');
      const payload = this.buildUpdatePayload(formData, assetId);
      console.log('PUBLISH-ASSETS: Update payload:', payload);
      console.log('PUBLISH-ASSETS: Update payload (JSON):', JSON.stringify(payload, null, 2));

      console.log('PUBLISH-ASSETS: Calling catalogueApiService.updateItem...');
      const response = await this.catalogueApiService.updateItem(payload);
      console.log('PUBLISH-ASSETS: Update response:', response);
      console.log('PUBLISH-ASSETS: Update response (JSON):', JSON.stringify(response, null, 2));
      
      this.toasterService.success('Asset metadata updated successfully');
      this.loadPublishAssets();
      this.closeModifyModal();
      console.log('=== PUBLISH-ASSETS: Update completed successfully ===');
    } catch (error) {
      console.error('=== PUBLISH-ASSETS: Error updating asset ===', error);
      this.toasterService.error('Failed to update asset. Please try again.');
    }
  }

  /**
   * Build payload for updateItem API
   */
  private buildUpdatePayload(formData: any, assetId: string): any {
    console.log('PUBLISH-ASSETS: buildUpdatePayload - formData:', formData);
    console.log('PUBLISH-ASSETS: buildUpdatePayload - assetId:', assetId);

    const isModel = this.getAssetTypeForModify() === 'model';
    const originalData = this.currentModifyAsset?.originalData || {};

    console.log('PUBLISH-ASSETS: buildUpdatePayload - isModel:', isModel);
    console.log('PUBLISH-ASSETS: buildUpdatePayload - originalData:', originalData);

    // type from table (API/row) – ensure array e.g. ["adex:DataBank"] or ["adex:AiModel"]
    const rawType = originalData.type;
    const typeArray = Array.isArray(rawType) ? rawType : (rawType ? [rawType] : []);

    // Build base payload
    const payload: any = {
      id: assetId,
      type: typeArray.length > 0 ? typeArray : (isModel ? ['adex:AiModel'] : ['adex:DataBank']),
      name: formData.title || originalData.name,
      description: formData.longDescription || formData.description || '',
      shortDescription: formData.shortDescription || '',
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      license: formData.license || '',
      industry: formData.industry || '',
      // Preserve existing fields
      organizationId: originalData.organizationId,
      organizationType: originalData.organizationType,
      uploadedBy: originalData.uploadedBy,
      organization: originalData.organization,
      department: originalData.department
    };

    if (isModel) {
      payload.modelType = formData.datasetType || originalData.modelType;
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.fileSize = originalData.fileSize || '';
      payload.mediaURL = formData.sampleDataUrl || '';
    } else {
      payload.geoCoverage = formData.geoCoverage || '';
      payload.yearRange = this.formatYearRangeAsString(formData.yearRange);
      payload.resourceServer = this.buildResourceServerArray(formData.resourceServer || '');
      payload.uploadFrequency = formData.uploadFrequency || '';
      payload.dataBankType = formData.datasetType || '';
      payload.fileFormat = (formData.fileFormat || '').toString().toLowerCase();
      payload.dataDescriptor = this.normalizeDataDescriptorToObject(formData.dataDescriptors);
    }

    // Map access policy
    const permission = formData.permission || 'Open';
    payload.accessPolicy = permission === 'Open' ? 'OPEN' : 
                           permission === 'Restricted' ? 'RESTRICTED' : 'PRIVATE';

    console.log('PUBLISH-ASSETS: buildUpdatePayload - final payload:', payload);
    return payload;
  }

  /**
   * Format yearRange for update payload as string "2020-2021" (API expects string format).
   */
  private formatYearRangeAsString(yearRange: any): string | null {
    if (yearRange == null) return null;
    if (typeof yearRange === 'string') return yearRange.trim() || null;
    if (typeof yearRange === 'object' && yearRange.startYear != null && yearRange.endYear != null) {
      return `${yearRange.startYear}-${yearRange.endYear}`;
    }
    return null;
  }

  /**
   * Ensure dataDescriptor is always an object (never an empty array).
   * Converts descriptors array to object format: { [attributeName]: { parameterType, schemaType } }.
   */
  private normalizeDataDescriptorToObject(dataDescriptors: any): Record<string, any> {
    if (dataDescriptors && typeof dataDescriptors === 'object' && !Array.isArray(dataDescriptors)) {
      return dataDescriptors;
    }
    const list = Array.isArray(dataDescriptors) ? dataDescriptors : [];
    const obj: Record<string, any> = {};
    list.forEach((desc: any) => {
      if (desc && typeof desc === 'object' && desc.attributeName) {
        obj[desc.attributeName] = {
          parameterType: desc.parameterType || '',
          schemaType: desc.schemaType || ''
        };
      }
    });
    return obj;
  }

  /**
   * Build resourceServer array for update payload from dropdown value (URL string).
   * Matches options in preview-metadata: OGC, NGSI-LD, GATEWAY; otherwise FILE/file.
   */
  private buildResourceServerArray(urlFromDropdown: string): any[] {
    const defaultId = 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1';
    const base = {
      id: defaultId,
      queryTypes: ['ATTR'],
      accessTypes: ['ATTR'],
      url: urlFromDropdown || ''
    };
    if (!urlFromDropdown || typeof urlFromDropdown !== 'string') {
      return [{ ...base, name: 'FILE', type: 'file' }];
    }
    const u = urlFromDropdown.toLowerCase();
    if (u.includes('ogc')) {
      return [{ ...base, name: 'OGC', type: 'ogc' }];
    }
    if (u.includes('rs.iudx') || u.includes('ngsi')) {
      return [{ ...base, name: 'NGSI-LD', type: 'ngsi-ld' }];
    }
    if (u.includes('rs-proxy') || u.includes('gateway')) {
      return [{ ...base, name: 'GATEWAY', type: 'gateway' }];
    }
    return [{ ...base, name: 'FILE', type: 'file' }];
  }

  /**
   * Handle form cancel from preview-metadata component
   */
  onModifyFormCancel(): void {
    this.closeModifyModal();
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }
}
